import { buildStrapiPublicDlUrl, filesPublicApiOriginFromEnv } from './strapiPublicFileUrl';

export type StrapiFolderImage = {
    url: string;
    name?: string;
    documentId?: string;
};

const AUDIO_FILE_NAME_PATTERN = /\.(mp3|ogg|wav|m4a|aac|webm|flac)(\?.*)?$/i;

function normalizeStrapiAuthHeader(token: string): string {
    const trimmed = token.trim();
    if (!trimmed) return '';
    return trimmed.toLowerCase().startsWith('bearer ') ? trimmed : `Bearer ${trimmed}`;
}

function strapiFolderImageFromRaw(file: unknown, filesOrigin: string): StrapiFolderImage[] {
    if (!file || typeof file !== 'object') return [];
    const record = file as { url?: unknown; name?: unknown; documentId?: unknown };
    const documentId = String(record.documentId ?? '').trim();
    const name = String(record.name ?? '').trim();
    const rawUrl = String(record.url ?? '').trim();
    const publicUrl = documentId ? buildStrapiPublicDlUrl(documentId, { origin: filesOrigin }) : '';
    const url = publicUrl || rawUrl;
    if (!url) return [];
    return [{ url, name: name || undefined, documentId: documentId || undefined }];
}

function filesFromStrapiListResponse(raw: unknown, filesOrigin: string): StrapiFolderImage[] {
    if (!raw || typeof raw !== 'object') return [];
    const root = raw as Record<string, unknown>;
    const files = Array.isArray(root.files)
        ? root.files
        : root.data && typeof root.data === 'object' && Array.isArray((root.data as { files?: unknown }).files)
            ? (root.data as { files: unknown[] }).files
            : [];
    return files.flatMap(file => strapiFolderImageFromRaw(file, filesOrigin));
}

function isStrapiAudioFile(file: StrapiFolderImage): boolean {
    const candidate = (file.name ?? file.url).trim();
    if (!candidate) return false;
    return AUDIO_FILE_NAME_PATTERN.test(candidate);
}

export type StrapiWebGameFolderListResult = {
    images: StrapiFolderImage[];
    message: string | null;
};

export type StrapiWebGameFolderAudioListResult = {
    audio: StrapiFolderImage[];
    message: string | null;
};

async function listStrapiWebGameFolderFiles(): Promise<StrapiWebGameFolderListResult> {
    const base = (process.env.REACT_APP_STRAPI_API_URL || '').replace(/\/?$/, '');
    const token = process.env.REACT_APP_STRAPI_API_AUTH_TOKEN || '';
    if (!base || !token) {
        return {
            images: [],
            message: 'Strapi folder listing is disabled. Set REACT_APP_STRAPI_API_URL and REACT_APP_STRAPI_API_AUTH_TOKEN.',
        };
    }

    const configured = (process.env.REACT_APP_STRAPI_WEBGAMES_FOLDER || '').trim();
    const folderCandidates: string[] = [];
    if (configured) folderCandidates.push(configured);
    for (const fallback of ['webgames', 'WebGames', 'webgames-generated', 'API Uploads', 'Sports', 'Competitions']) {
        if (!folderCandidates.includes(fallback)) folderCandidates.push(fallback);
    }

    const merged = new Map<string, StrapiFolderImage>();
    const emptyFolders: string[] = [];
    const failedFolders: string[] = [];
    const filesOrigin = filesPublicApiOriginFromEnv();

    for (const docName of folderCandidates) {
        const url = `${base}/file-system/docs/${encodeURIComponent(docName)}`;
        try {
            const response = await fetch(url, {
                headers: {
                    Authorization: normalizeStrapiAuthHeader(token),
                },
            });
            if (!response.ok) {
                failedFolders.push(docName);
                continue;
            }
            const raw: unknown = await response.json();
            const images = filesFromStrapiListResponse(raw, filesOrigin);
            if (images.length === 0) {
                emptyFolders.push(docName);
                continue;
            }
            images.forEach(image => {
                const normalizedUrl = image.url.trim();
                if (!normalizedUrl || merged.has(normalizedUrl)) return;
                merged.set(normalizedUrl, { ...image, url: normalizedUrl });
            });
        } catch {
            failedFolders.push(docName);
        }
    }

    const images = Array.from(merged.values());
    if (images.length > 0) {
        return { images, message: null };
    }

    if (emptyFolders.length > 0) {
        return {
            images: [],
            message: `Strapi folders exist but have no files yet (${emptyFolders.join(', ')}). Add images in the CMS or set REACT_APP_STRAPI_WEBGAMES_FOLDER.`,
        };
    }

    const tried = folderCandidates
        .map(folder => (failedFolders.includes(folder) ? `"${folder}"` : null))
        .filter(Boolean)
        .join(', ');
    return {
        images: [],
        message: tried
            ? `Strapi: no Web Games image folders could be loaded. Tried ${tried}. Create a CMS media folder or set REACT_APP_STRAPI_WEBGAMES_FOLDER.`
            : 'Strapi: no Web Games image folders could be loaded. Create a CMS media folder or set REACT_APP_STRAPI_WEBGAMES_FOLDER.',
    };
}

export async function listStrapiWebGameFolderImages(): Promise<StrapiWebGameFolderListResult> {
    return listStrapiWebGameFolderFiles();
}

export async function listStrapiWebGameFolderAudio(): Promise<StrapiWebGameFolderAudioListResult> {
    const result = await listStrapiWebGameFolderFiles();
    return {
        audio: result.images.filter(isStrapiAudioFile),
        message: result.message,
    };
}
